import ChartistAxisTitle from 'chartist-plugin-axistitle';
import ChartistTooltip from 'chartist-plugin-tooltips-updated';
import { makeStyles } from '@mui/styles';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import {
  AxisTitleOptions,
  BarChartComp,
  BarChartOptions,
  LineChartComp,
  LineChartOptions,
  OnChartCreated,
  OnChartDraw,
  PieChartComp,
  PieChartOptions,
} from './types';

type Chart = BarChartComp | LineChartComp | PieChartComp | null;

export const useStylesBase = makeStyles((theme: MasonTheme) => ({
  chartBase: {
    '& .ct-label': {
      color: theme.palette.grey[800],
    },
    '& .chartist-tooltip': {
      position: 'absolute',
      display: 'inline-block',
      opacity: 0,
      minWidth: 'fit-content',
      padding: theme.spacing(0.75, 1, 0.25, 1),
      background: theme.palette.grey[800],
      border: 'none',
      color: theme.palette.common.white,
      borderRadius: theme.spacing(0.25),
      width: 80,
      height: 52,
      textAlign: 'center',
      pointerEvents: 'none',
      zIndex: 1,
      transition: `opacity ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeIn}`,
      ...theme.typography.subtitle1,
      fontSize: '14px',
      '&:before, &:after': {
        content: '""',
        position: 'absolute',
        top: '95%',
        left: '50%',
        width: 0,
        height: 0,
        marginLeft: -12,
        border: '12px solid transparent',
      },
      '&:before': {
        borderTopColor: theme.palette.grey[800],
        zIndex: 1,
      },
      '&:after': {
        marginTop: 1,
      },
      '&.tooltip-show': {
        opacity: 1,
      },
      '&.chartist-tooltip-meta': {
        color: theme.palette.common.white,
      },
      '&.chartist-tooltip-value': {
        color: theme.palette.common.white,
      },
    },
  },
}));

export function addPlugins(chartOptions?: BarChartOptions | LineChartOptions | PieChartOptions) {
  if (!chartOptions) {
    return;
  }

  chartOptions.plugins = [];

  if ('axisXTitleOptions' in chartOptions || 'axisYTitleOptions' in chartOptions) {
    const axisXTitleOptions = chartOptions['axisXTitleOptions'];
    const axisYTitleOptions = chartOptions['axisYTitleOptions'];

    let yTitleOptions: AxisTitleOptions | null = null;

    if (axisYTitleOptions) {
      const { flipTitle, offset } = axisYTitleOptions;

      if (offset || flipTitle !== undefined) {
        //
        // we invert the flipTitle and offset props that pertain
        // to the Y-Axis Title because the plugin design is
        // counterintuitive; so, we need to swap them back here
        // to be re-orient how the plugin expects them
        //
        yTitleOptions = {
          ...axisYTitleOptions,
          ...{
            flipTitle: flipTitle !== undefined ? !flipTitle : true,
            offset: offset
              ? {
                  x: offset.y,
                  y: offset.x,
                }
              : undefined,
          },
        };
      }
    }

    chartOptions.plugins?.push(
      ChartistAxisTitle({
        axisX: axisXTitleOptions,
        axisY: yTitleOptions,
      })
    );
  }

  chartOptions.plugins.push(
    ChartistTooltip({
      appendToBody: false,
      transformTooltipTextFnc: chartOptions.onTransformTooltipText,
    })
  );
}

export function createEventHandlers(
  chart: Chart,
  onCreated: OnChartCreated | undefined,
  onDraw: OnChartDraw | undefined
) {
  if (chart && onDraw) {
    chart.on('draw', onDraw);
  }
  if (chart && onCreated) {
    chart.on('created', onCreated);
  }
}

export function disposeEventHandlers(
  chart: Chart,
  onCreated: OnChartCreated | undefined,
  onDraw: OnChartDraw | undefined
) {
  if (chart && onCreated) {
    chart.off('created', onCreated);
  }
  if (chart && onDraw) {
    chart.off('draw', onDraw);
  }
}
